import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import { withTranslation } from 'react-i18next';
import {
    Box,
    DaInput,
    SearchIcon,
    InternalGrid,
    ModalProvider,
    ModalControl,
} from '@paygreen/paygreen-ui';
import { IsBigDesktop } from '../../utils/tools';
import { PaginationTable } from '../../components';
import CategoriesDaTable from './CategoriesDaTable';
import ModifyProduct from '../matching/modifyProduct/ModifyProduct';
import { useDebounce } from '../../utils/hooks';

const CategoriesTable = ({
    t,
    data,
    filterCategories,
    isLoading,
    filters,
    setFilters,
    handleRefreshData,
    refresh,
    resetDebounce,
    setIsLoading,
}) => {
    const debouncedFilters = useDebounce(filters, 500, resetDebounce);
    const [modalData, setModalData] = useState();

    const handleCategoryName = useCallback(
        (e) => {
            const { value } = e.target;

            if (
                // we start research only with minimum of 3 characters and when back to empty input
                !value.length ||
                value.length > 2
            ) {
                setIsLoading(true);
                setFilters({
                    ...filters,
                    categoryName: value,
                    activePageReset: true,
                });
            }
        },
        [setFilters, filters, setIsLoading],
    );

    const columns = useMemo(() => {
        return [
            {
                Header: t('categories.table.id'),
                accessor: 'id',
            },
            {
                Header: t('categories.table.name'),
                accessor: 'categoryName',
                Filter: (
                    <DaInput
                        id="searchInput"
                        onChange={handleCategoryName}
                        placeholder={t('user.list.table.input')}
                        fieldSize="sm"
                        icon={<SearchIcon />}
                        defaultValue={filters.externalProductCategory}
                    />
                ),
            },
            {
                Header: 'Actions',
                accessor: '',
            },
        ];
    }, [filters, handleCategoryName, t]);

    const {
        headerGroups,
        prepareRow,
        page,
        pageCount,
        gotoPage,
        setPageSize,
        state: { pageIndex, selectedRowIds },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 25 },
            autoResetPage: true,
            manualSortBy: true,
            autoResetFilters: true,
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        filterCategories({
            debouncedFilters,
        });
    }, [filterCategories, debouncedFilters]);

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            gridTemplateColumns="1fr"
            blockPadding="none"
            justifyItems={IsBigDesktop() ? 'start' : 'stretch'}
            hasStaticWidth={IsBigDesktop() ? false : true}
        >
            <ModalProvider>
                <Box marginTop="md">
                    <CategoriesDaTable
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        isLoading={isLoading}
                        data={data}
                        selectedRowIds={selectedRowIds}
                        handleRefreshData={handleRefreshData}
                        setModalData={setModalData}
                    />

                    {!!pageCount && (
                        <PaginationTable
                            setPageSize={setPageSize}
                            pageCount={pageCount}
                            pageIndex={pageIndex}
                            gotoPage={gotoPage}
                        />
                    )}
                </Box>

                <ModalControl>
                    <ModifyProduct
                        productData={modalData?.categoryData}
                        mode={modalData?.mode}
                        handleRefreshData={handleRefreshData}
                        openMatching={modalData?.openMatching}
                        isCategory
                    />
                </ModalControl>
            </ModalProvider>
        </InternalGrid>
    );
};

CategoriesTable.propTypes = {
    data: PropTypes.array.isRequired,
    filterCategories: PropTypes.func.isRequired,
    pageCount: PropTypes.number,
    isLoading: PropTypes.bool,
    totalItems: PropTypes.number,
    setSelectedRows: PropTypes.func,
    filters: PropTypes.object,
    setFilters: PropTypes.func,
    handleCategoryName: PropTypes.func,
    refresh: PropTypes.bool,
    resetDebounce: PropTypes.bool,
    setIsLoading: PropTypes.func,
};

CategoriesTable.defaultProps = {
    isLoading: false,
    pageCount: 0,
    refresh: false,
    resetDebounce: false,
};

export default withTranslation('common')(CategoriesTable);
